import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Monitor, RefreshCw, ExternalLink, Copy, Zap } from "lucide-react";
import { toast } from "sonner";
import { getLanguageName } from "./constants";
import { OBSTutorialModal } from "./OBSTutorialModal";
import { useLanguage } from "@/i18n/LanguageContext";

export function OBSIntegrationCard({
  isPro,
  obsSettings,
  setObsSettings,
  generatedObsUrl,
  setGeneratedObsUrl,
  secondWindowEnabled,
  secondTargetLang,
  translationTargetLang
}) {
  const { t } = useLanguage();
  
  const bgOptions = [
    { value: "transparent", label: t("transparent"), color: "bg-zinc-800 border-2 border-dashed border-zinc-600" },
    { value: "green", label: t("green"), color: "bg-green-500" },
    { value: "blue", label: t("blue"), color: "bg-blue-500" },
    { value: "magenta", label: t("magenta"), color: "bg-pink-500" },
    { value: "black", label: t("black"), color: "bg-black border border-zinc-600" }
  ];

  const colorOptions = [
    { value: "#ffffff", label: "White" },
    { value: "#ffff00", label: "Yellow" },
    { value: "#00ff00", label: "Green" },
    { value: "#00ffff", label: "Cyan" },
    { value: "#ff6b6b", label: "Red" }
  ];

  const positionOptions = [
    { value: "top", label: t("top") },
    { value: "center", label: t("center") },
    { value: "bottom", label: t("bottom") }
  ];

  const generateUrl = () => {
    const baseUrl = obsSettings.windowType === "second" ? "/translation2" : "/translation";
    const params = new URLSearchParams();
    params.set("mode", "obs");
    params.set("bg", obsSettings.bg);
    params.set("fontSize", obsSettings.fontSize.toString());
    params.set("color", obsSettings.color.replace("#", ""));
    params.set("position", obsSettings.position);
    params.set("shadow", obsSettings.shadow.toString());
    const url = `${window.location.origin}${baseUrl}?${params.toString()}`;
    setGeneratedObsUrl(url);
  };

  return (
    <Card className={`col-span-12 bento-card ${!isPro ? 'opacity-60' : ''}`} data-testid="obs-integration-card">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
          <Monitor className="w-5 h-5" />
          {t("obsIntegration")}
          {isPro ? (
            <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">
              {t("streaming")}
            </span>
          ) : (
            <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
              PRO
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {isPro ? (
          <>
            <p className="text-zinc-400 text-sm">
              {t("obsDescription")}
            </p>
            
            {/* Customization Controls */}
            <div className="space-y-4 p-4 bg-zinc-800/30 rounded-lg border border-zinc-700">
              <h4 className="text-sm font-medium text-zinc-300">{t("customizeLegend")}</h4>
              
              {/* Window Type Selection */}
              {secondWindowEnabled && secondTargetLang && (
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs">Window</Label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={obsSettings.windowType === "main" ? "default" : "outline"}
                      onClick={() => setObsSettings(prev => ({ ...prev, windowType: "main" }))}
                      className={obsSettings.windowType === "main" 
                        ? "bg-purple-600 hover:bg-purple-700 text-white" 
                        : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"}
                    >
                      Main ({getLanguageName(translationTargetLang)})
                    </Button>
                    <Button
                      size="sm"
                      variant={obsSettings.windowType === "second" ? "default" : "outline"}
                      onClick={() => setObsSettings(prev => ({ ...prev, windowType: "second" }))}
                      className={obsSettings.windowType === "second" 
                        ? "bg-purple-600 hover:bg-purple-700 text-white" 
                        : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"}
                    >
                      Second ({getLanguageName(secondTargetLang)})
                    </Button>
                  </div>
                </div>
              )}
          
              {/* Background Color */}
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">{t("backgroundColor")}</Label>
                <div className="flex flex-wrap gap-2">
                  {bgOptions.map(bg => (
                    <button
                      key={bg.value}
                      onClick={() => setObsSettings(prev => ({ ...prev, bg: bg.value }))}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-all ${
                        obsSettings.bg === bg.value 
                          ? "ring-2 ring-purple-500 ring-offset-2 ring-offset-zinc-900" 
                          : "hover:ring-1 hover:ring-zinc-600"
                      }`}
                    >
                      <div className={`w-4 h-4 rounded ${bg.color}`} />
                      <span className="text-xs text-zinc-300">{bg.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Font Size Slider */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label className="text-zinc-400 text-xs">{t("fontSize")}</Label>
                  <span className="text-xs text-purple-400">{obsSettings.fontSize}px</span>
                </div>
                <input
                  type="range"
                  min="24"
                  max="120"
                  value={obsSettings.fontSize}
                  onChange={(e) => setObsSettings(prev => ({ ...prev, fontSize: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                />
                <div className="flex justify-between text-xs text-zinc-500">
                  <span>24px</span>
                  <span>120px</span>
                </div>
              </div>
              
              {/* Text Color */}
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">{t("textColor")}</Label>
                <div className="flex items-center gap-3">
                  <div className="flex gap-2">
                    {colorOptions.map(color => (
                      <button
                        key={color.value}
                        onClick={() => setObsSettings(prev => ({ ...prev, color: color.value }))}
                        className={`w-8 h-8 rounded-full transition-all ${
                          obsSettings.color === color.value 
                            ? "ring-2 ring-purple-500 ring-offset-2 ring-offset-zinc-900" 
                            : "hover:ring-1 hover:ring-zinc-600"
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.label}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={obsSettings.color}
                      onChange={(e) => setObsSettings(prev => ({ ...prev, color: e.target.value }))}
                      className="w-8 h-8 rounded cursor-pointer bg-transparent"
                    />
                    <span className="text-xs text-zinc-500">{t("obsCustomizeLabel")}</span>
                  </div>
                </div>
              </div>
              
              {/* Position */}
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">{t("obsPositionLabel")}</Label>
                <div className="flex gap-2">
                  {positionOptions.map(pos => (
                    <Button
                      key={pos.value}
                      size="sm"
                      variant={obsSettings.position === pos.value ? "default" : "outline"}
                      onClick={() => setObsSettings(prev => ({ ...prev, position: pos.value }))}
                      className={obsSettings.position === pos.value 
                        ? "bg-purple-600 hover:bg-purple-700 text-white flex-1" 
                        : "border-zinc-700 text-zinc-300 hover:bg-zinc-800 flex-1"}
                    >
                      {pos.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Shadow Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-zinc-300 text-sm">{t("obsTextShadowLabel")}</Label>
                  <p className="text-xs text-zinc-500">{t("obsTextShadowDesc")}</p>
                </div>
                <Switch
                  checked={obsSettings.shadow}
                  onCheckedChange={(checked) => setObsSettings(prev => ({ ...prev, shadow: checked }))}
                />
              </div>
              
              {/* Generate URL Button */}
              <Button
                onClick={generateUrl}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t("obsGenerateUrl")}
              </Button>
            </div>
            
            {/* Generated URL Display */}
            {generatedObsUrl && (
              <div className="space-y-2 p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                <Label className="text-purple-300 text-xs">{t("obsGeneratedUrlLabel")}</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-zinc-900 px-3 py-2 rounded text-xs text-purple-300 break-all">
                    {generatedObsUrl}
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      navigator.clipboard.writeText(generatedObsUrl);
                      toast.success(t("obsCopied"));
                    }}
                    className="h-8 w-8 p-0 text-purple-400 hover:text-purple-300"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(generatedObsUrl, "_blank")}
                  className="w-full border-purple-500/50 text-purple-300 hover:bg-purple-500/20"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {t("obsPreviewBtn")}
                </Button>
              </div>
            )}
            
            {/* Parameter Reference */}
            <div className="pt-3 border-t border-zinc-800">
              <details className="group">
                <summary className="text-zinc-500 text-xs cursor-pointer hover:text-zinc-400 flex items-center gap-1">
                  <span className="group-open:rotate-90 transition-transform">▶</span>
                  {t("obsParamsReference")}
                </summary>
                <ul className="text-zinc-500 text-xs space-y-1 mt-2 pl-4">
                  <li>• <code className="text-purple-400">bg=transparent|green|blue|black</code> - {t("obsParamBg")}</li>
                  <li>• <code className="text-purple-400">fontSize=48</code> - {t("obsParamFontSize")}</li>
                  <li>• <code className="text-purple-400">color=ffffff</code> - {t("obsParamColor")}</li>
                  <li>• <code className="text-purple-400">position=bottom|center|top</code> - {t("obsParamPosition")}</li>
                  <li>• <code className="text-purple-400">shadow=true|false</code> - {t("obsParamShadow")}</li>
                </ul>
              </details>
            </div>
            
            {/* Tutorial Button */}
            <div className="flex justify-end pt-3">
              <OBSTutorialModal />
            </div>
          </>
        ) : (
          <div className="relative">
            <div className="absolute inset-0 bg-zinc-900/80 backdrop-blur-sm rounded-lg z-10 flex flex-col items-center justify-center">
              <p className="text-zinc-400 text-sm text-center px-4 mb-4">
                {t("obsProOverlayDesc")}
              </p>
              <p className="text-zinc-500 text-xs text-center mb-4">
                {t("obsProOnlyText")}
              </p>
              <Link to="/pricing">
                <Button className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold">
                  <Zap className="w-4 h-4 mr-2" />
                  {t("upgradePro")}
                </Button>
              </Link>
            </div>
            <div className="opacity-30 pointer-events-none">
              <p className="text-zinc-400 text-sm mb-4">
                {t("obsDescription")}
              </p>
              <div className="space-y-4 p-4 bg-zinc-800/30 rounded-lg border border-zinc-700">
                <h4 className="text-sm font-medium text-zinc-300">{t("customizeLegend")}</h4>
                <div className="h-32 flex items-center justify-center text-zinc-600 text-sm">
                  {t("obsSettingsPlaceholder")}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
